/**
 * @component CrmImportPanel
 * @description CRM 會員匯出檔匯入（A-170 Phase 1，journey 1）：
 * 上傳 → 預覽（不寫入）→ 逐列處理需處理的列 → 確認（整批寫入）→ 結果。
 * 身分證字號只以遮罩顯示；確認時送出同一檔案與預覽的 planHash。
 */

import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  ALERT,
  CHECK_LABEL,
  CHECKBOX,
  DROPZONE,
  FIELD,
  NOTE,
  OUTLINE,
  PRIMARY,
} from './importStyles';
import type {
  CrmImportDecisionDto,
  CrmImportMemberDto,
  CrmImportPreviewDto,
  CrmImportPreviewRowDto,
  CrmImportResultDto,
  CrmProfileField,
} from '@pokerroom/types';

type RowChoice = {
  skip: boolean;
  identity?: 'new' | 'member';
  memberNo?: string;
  /** Lookup of memberNo, so staff see the member and its blacklist before confirming. */
  lookup?: CrmImportMemberDto;
  venueId?: string;
  levelId?: string;
  fields?: CrmProfileField[];
};

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const has = (row: CrmImportPreviewRowDto, code: string) => row.reasons.some((r) => r.code === code);
const venueReason = (row: CrmImportPreviewRowDto) =>
  has(row, 'VENUE_UNKNOWN') || has(row, 'VENUE_NOT_PERMITTED');
const needsLevel = (row: CrmImportPreviewRowDto) => has(row, 'LEVEL_UNKNOWN') || venueReason(row);
const needsDecision = (row: CrmImportPreviewRowDto) =>
  row.reasons.length > 0 || row.identity === 'changed';

function errorMessage(error: unknown): string | undefined {
  return (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
}

export default function CrmImportPanel({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation('member');
  const { t: tc } = useTranslation('common');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CrmImportPreviewDto | null>(null);
  const [choices, setChoices] = useState<Record<string, RowChoice>>({});
  const [result, setResult] = useState<CrmImportResultDto | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setChoices({});
    setResult(null);
  };

  const levelByName = useCallback(
    (venueId: string | undefined, name: string | null) =>
      preview?.levels.find((l) => (!l.venueId || l.venueId === venueId) && l.displayName === name)
        ?.id,
    [preview],
  );

  const dryRun = useMutation({
    mutationFn: (selected: File) => {
      const form = new FormData();
      form.append('file', selected);
      return api.post<CrmImportPreviewDto>('/members/import/crm/dry-run', form, {
        ...MULTIPART,
        timeout: 25_000,
      });
    },
    onSuccess: (data) => {
      setPreview(data);
      setChoices(
        Object.fromEntries(
          data.rows.map((row) => [
            row.sourceKey,
            {
              skip: has(row, 'INVALID_VALUE'),
              levelId: row.levelId ?? undefined,
              // Clearing a value is never preselected; staff tick it explicitly.
              fields: row.changedFields
                .filter((c) => c.selectable && c.incoming !== null)
                .map((c) => c.field),
            },
          ]),
        ),
      );
    },
  });

  const confirm = useMutation({
    mutationFn: (decisions: CrmImportDecisionDto[]) => {
      const form = new FormData();
      form.append('file', file!);
      form.append('payload', JSON.stringify({ planHash: preview!.planHash, decisions }));
      return api.post<CrmImportResultDto>('/members/import/crm/confirm', form, {
        ...MULTIPART,
        timeout: 25_000,
      });
    },
    onSuccess: (data) => setResult(data),
  });

  const complete = (row: CrmImportPreviewRowDto, choice: RowChoice | undefined) => {
    if (!choice) return !needsDecision(row);
    if (choice.skip) return true;
    if (has(row, 'INVALID_VALUE')) return false;
    if (row.identity === null) {
      if (!choice.identity) return false;
      if (
        choice.identity === 'member' &&
        (!choice.memberNo?.trim() || choice.lookup?.memberNo !== choice.memberNo.trim())
      ) {
        return false;
      }
    }
    if (venueReason(row) && !choice.venueId) return false;
    if (!preview?.associationMode && needsLevel(row) && !choice.levelId) return false;
    return true;
  };

  const pending = preview
    ? preview.rows.filter((row) => !complete(row, choices[row.sourceKey]))
    : [];

  const decisions = (): CrmImportDecisionDto[] =>
    preview!.rows.flatMap((row): CrmImportDecisionDto[] => {
      const choice = choices[row.sourceKey];
      if (choice?.skip) return [{ sourceKey: row.sourceKey, action: 'skip' }];
      if (!needsDecision(row)) return [];
      return [
        {
          sourceKey: row.sourceKey,
          action: 'apply',
          ...(row.identity === null && choice?.identity === 'new' ? { createNew: true } : {}),
          ...(row.identity === null && choice?.identity === 'member'
            ? {
                memberNo: choice.memberNo!.trim(),
                blacklistedVenues: choice.lookup!.blacklistedVenues,
              }
            : {}),
          ...(venueReason(row) ? { venueId: choice?.venueId } : {}),
          ...(!preview?.associationMode && needsLevel(row) ? { levelId: choice?.levelId } : {}),
          ...(row.identity === 'changed' ? { fields: choice?.fields ?? [] } : {}),
        },
      ];
    });

  const update = (key: string, patch: Partial<RowChoice>) =>
    setChoices((all) => ({ ...all, [key]: { ...(all[key] ?? { skip: false }), ...patch } }));

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted[0]) {
      setFile(accepted[0]);
      setPreview(null);
      setResult(null);
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  if (result) {
    const items: [string, number][] = [
      ['crmResultCreated', result.createdMembers],
      ['crmResultMatched', result.matchedMembers],
      ['crmResultUpdated', result.updatedMembers],
      ['crmResultMemberships', result.createdMemberships],
      ['crmResultSkipped', result.skippedRows],
      ['crmResultUnchanged', result.unchangedRows],
    ];
    return (
      <div data-testid="crm-import-result">
        <h2 className="text-lg font-semibold text-base-content mb-3">{t('crmResultTitle')}</h2>
        <CountGrid items={items} className="grid-cols-2 sm:grid-cols-3 mb-6" />
        <div className="flex flex-wrap justify-end gap-3">
          <button type="button" onClick={reset} className={OUTLINE}>
            {t('crmRestart')}
          </button>
          <button type="button" onClick={onDone} className={PRIMARY}>
            {t('done')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`${NOTE} mb-6 p-4`}>
        <p className="font-semibold mb-1">{t('crmGuideTitle')}</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>{t('crmGuideColumns')}</li>
          <li>{t('crmGuideMatch')}</li>
          <li>{t('crmGuidePrivacy')}</li>
          <li>{t('crmGuideDryRun')}</li>
        </ul>
      </div>

      {!preview && (
        <>
          <div
            {...getRootProps({ role: 'button' })}
            className={`${DROPZONE} ${isDragActive ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-primary'}`}
          >
            <input {...getInputProps()} aria-label={t('crmMode')} data-testid="crm-import-file" />
            <p className="text-base-content font-medium">
              {isDragActive ? t('dropToUpload') : t('dragDropExcel')}
            </p>
            <p className="text-base-content/80 text-sm">{t('clickToSelect')}</p>
          </div>
          {file && (
            <p className="mt-3 text-sm text-base-content break-all tabular-nums">
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => file && dryRun.mutate(file)}
              disabled={!file || dryRun.isPending}
              className={PRIMARY}
            >
              {dryRun.isPending ? tc('loading') : t('crmPreview')}
            </button>
          </div>
          {dryRun.error && (
            <p role="alert" className={`${ALERT} mt-3`}>
              {errorMessage(dryRun.error) ?? t('formErrorRetry')}
            </p>
          )}
        </>
      )}

      {preview && (
        <div data-testid="crm-import-preview">
          <CountGrid
            items={[
              ['crmSummaryTotal', preview.summary.total],
              ['crmSummaryNew', preview.summary.new],
              ['crmSummaryMatched', preview.summary.matched],
              ['crmSummaryChanged', preview.summary.changed],
              ['crmSummaryUnchanged', preview.summary.unchanged],
              ['crmSummaryReview', preview.summary.review],
            ]}
            className="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-4"
          />

          {preview.rows.length === 0 ? (
            <p className="text-sm text-base-content mb-4">{t('crmNoRows')}</p>
          ) : (
            <ul className="space-y-3 mb-4">
              {preview.rows.map((row) => (
                <CrmRow
                  key={row.sourceKey}
                  row={row}
                  choice={choices[row.sourceKey]}
                  preview={preview}
                  levelByName={levelByName}
                  onChange={(patch) => update(row.sourceKey, patch)}
                />
              ))}
            </ul>
          )}

          {pending.length > 0 && (
            <p className="mb-3 border border-warning px-3 py-2 text-sm text-base-content">
              {t('crmPendingDecisions', { count: pending.length })}
            </p>
          )}
          {confirm.error && (
            <p role="alert" className={`${ALERT} mb-3`}>
              {errorMessage(confirm.error) ?? t('formErrorRetry')}
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <button type="button" onClick={reset} className={OUTLINE}>
              {t('crmRestart')}
            </button>
            <button
              type="button"
              onClick={() => confirm.mutate(decisions())}
              disabled={pending.length > 0 || confirm.isPending}
              className={PRIMARY}
              data-testid="crm-import-confirm"
            >
              {confirm.isPending ? tc('loading') : t('crmConfirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CountGrid({ items, className }: { items: [string, number][]; className: string }) {
  const { t } = useTranslation('member');
  return (
    <dl className={`grid gap-2 ${className}`}>
      {items.map(([key, value]) => (
        <div key={key} className="flex flex-col-reverse border border-base-300 bg-base-200 p-3">
          <dt className="text-sm text-base-content">{t(key)}</dt>
          <dd className="text-2xl font-bold text-base-content tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CrmRow({
  row,
  choice,
  preview,
  levelByName,
  onChange,
}: {
  row: CrmImportPreviewRowDto;
  choice: RowChoice | undefined;
  preview: CrmImportPreviewDto;
  levelByName: (venueId: string | undefined, name: string | null) => string | undefined;
  onChange: (patch: Partial<RowChoice>) => void;
}) {
  const { t } = useTranslation('member');
  const lookup = useMutation({
    mutationFn: (memberNo: string) =>
      api.get<CrmImportMemberDto>('/members/import/crm/member', { params: { memberNo } }),
    onSuccess: (data) => onChange({ lookup: data }),
  });
  const skip = choice?.skip ?? false;
  const invalid = has(row, 'INVALID_VALUE');
  const fieldList = (fields: string[]) => fields.map((f) => t(`crmField_${f}`)).join('、');
  const shown = (value: string | null) =>
    value === null ? t('crmEmpty') : value === 'unreadable' ? t('crmUnreadable') : value;
  const levelVenue = venueReason(row) ? choice?.venueId : (row.venueId ?? undefined);
  const statusTone =
    row.status === 'review'
      ? 'border-warning'
      : row.status === 'changed'
        ? 'border-info'
        : 'border-success';
  const id = `crm-row-${row.sourceKey}`;

  return (
    <li
      className={`relative border border-base-300 p-3 text-sm text-base-content ${skip ? 'border-dashed bg-base-200' : ''}`}
      data-testid={id}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="text-base-content/80">{t('rowNumber', { row: row.rowNumber })}</span>
        <span className="font-mono text-sm">{row.sourceKey}</span>
        <span className="font-medium">
          {row.nickname ?? '-'}
          {row.realName && row.realName !== row.nickname ? ` / ${row.realName}` : ''}
        </span>
        <span>{row.phone ?? '-'}</span>
        <span className="font-mono">{row.nationalIdMasked ?? '-'}</span>
        <span>
          {row.venueName ?? '-'} · {row.levelName ?? '-'}
        </span>
        <span className={`border px-2 text-sm font-medium ${statusTone}`}>
          {t(`crmStatus_${row.status}`)}
        </span>
      </div>

      <ul className="mt-2 space-y-1 text-sm text-base-content">
        {row.member && <li>{t('crmMatchedTo', { nickname: row.member.nickname })}</li>}
        {row.membership === 'create' && row.newMembershipStatus && (
          <li>{t('crmMembershipCreate', { status: t(`status_${row.newMembershipStatus}`) })}</li>
        )}
        {row.membership === 'exists' && <li>{t('crmMembershipExists')}</li>}
        {row.membershipDifferences.map((d) => (
          <li key={d.field} className="text-base-content/80">
            {t('crmMembershipDiff', {
              field: t(`crmDiffField_${d.field}`),
              crm: d.crm ?? t('crmEmpty'),
              ours: d.ours ?? t('crmEmpty'),
            })}
          </li>
        ))}
        {row.filledFields.length > 0 && (
          <li>{t('crmFilledFields', { fields: fieldList(row.filledFields) })}</li>
        )}
        {row.reasons.map((reason) => (
          <li key={reason.code} className="font-medium">
            {reason.code === 'BLACKLISTED' ? (
              <>
                {reason.crm &&
                  `${t('crmReason_BLACKLISTED')} — ${
                    row.membership === 'exists' ? t('crmBlacklistExisting') : t('crmBlacklistNew')
                  }`}
                {(reason.blacklistedVenues ?? 0) > 0 &&
                  ` ${t('crmBlacklistOurs', { count: reason.blacklistedVenues })}`}
                {(reason.otherVenues ?? 0) > 0 &&
                  ` ${t('crmBlacklistOtherVenues', { count: reason.otherVenues })}`}
              </>
            ) : (
              t(`crmReason_${reason.code}`, { fields: fieldList(reason.fields ?? []) })
            )}
          </li>
        ))}
      </ul>

      {needsDecision(row) && (
        <div className="mt-2 flex flex-col gap-2">
          <label className={CHECK_LABEL}>
            <input
              type="checkbox"
              className={CHECKBOX}
              checked={skip}
              disabled={invalid}
              onChange={(e) => onChange({ skip: e.target.checked })}
            />
            {t('crmSkip')}
          </label>

          {!skip && !invalid && row.identity === null && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className={CHECK_LABEL}>
                <input
                  type="radio"
                  className={CHECKBOX}
                  name={`identity-${row.sourceKey}`}
                  checked={choice?.identity === 'new'}
                  onChange={() => onChange({ identity: 'new' })}
                />
                {has(row, 'PHONE_CONFLICT') ? t('crmCreateNewNoPhone') : t('crmCreateNew')}
              </label>
              <label className={CHECK_LABEL}>
                <input
                  type="radio"
                  className={CHECKBOX}
                  name={`identity-${row.sourceKey}`}
                  checked={choice?.identity === 'member'}
                  onChange={() => onChange({ identity: 'member' })}
                />
                {t('crmMatchMember')}
              </label>
              {choice?.identity === 'member' && (
                <>
                  <label htmlFor={`${id}-member-no`} className="sr-only">
                    {t('memberNo')}
                  </label>
                  <input
                    id={`${id}-member-no`}
                    type="text"
                    value={choice.memberNo ?? ''}
                    onChange={(e) => onChange({ memberNo: e.target.value, lookup: undefined })}
                    placeholder="MB-2026-0001"
                    className={`${FIELD} max-w-48 font-mono`}
                  />
                  <button
                    type="button"
                    onClick={() => lookup.mutate(choice.memberNo!.trim())}
                    disabled={!choice.memberNo?.trim() || lookup.isPending}
                    className={OUTLINE}
                  >
                    {t('crmLookupMember')}
                  </button>
                </>
              )}
            </div>
          )}

          {!skip && !invalid && choice?.identity === 'member' && choice.lookup && (
            <p className="text-sm">
              {t('crmMatchedTo', { nickname: choice.lookup.nickname })}
              {choice.lookup.blacklistedVenues > 0 && (
                <span className="font-medium">
                  {' '}
                  {t('crmBlacklistOurs', { count: choice.lookup.blacklistedVenues })}
                </span>
              )}
            </p>
          )}
          {!skip && !invalid && choice?.identity === 'member' && lookup.error && (
            <p role="alert" className={ALERT}>
              {errorMessage(lookup.error) ?? t('formErrorRetry')}
            </p>
          )}

          {!skip && !invalid && venueReason(row) && (
            <select
              aria-label={t('crmChooseVenue')}
              value={choice?.venueId ?? ''}
              onChange={(e) =>
                onChange({
                  venueId: e.target.value || undefined,
                  levelId: levelByName(e.target.value, row.levelName),
                })
              }
              className={`${FIELD} max-w-xs`}
            >
              <option value="">{t('crmChooseVenue')}</option>
              {preview.venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          )}

          {!preview.associationMode && !skip && !invalid && needsLevel(row) && (
            <select
              aria-label={t('crmChooseLevel')}
              value={choice?.levelId ?? ''}
              disabled={!levelVenue}
              onChange={(e) => onChange({ levelId: e.target.value || undefined })}
              className={`${FIELD} max-w-xs`}
            >
              <option value="">{t('crmChooseLevel')}</option>
              {preview.levels
                .filter((l) => !l.venueId || l.venueId === levelVenue)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.displayName}
                  </option>
                ))}
            </select>
          )}

          {!skip && row.identity === 'changed' && row.changedFields.length > 0 && (
            <fieldset>
              <legend className="text-sm text-base-content mb-1">
                {t('crmChangedFieldsTitle')}
              </legend>
              {row.changedFields.map((c) => (
                <label key={c.field} className={`${CHECK_LABEL} flex-wrap`}>
                  <input
                    type="checkbox"
                    className={CHECKBOX}
                    disabled={!c.selectable}
                    checked={choice?.fields?.includes(c.field) ?? false}
                    onChange={(e) =>
                      onChange({
                        fields: e.target.checked
                          ? [...(choice?.fields ?? []), c.field]
                          : (choice?.fields ?? []).filter((f) => f !== c.field),
                      })
                    }
                  />
                  <span className="font-medium">{t(`crmField_${c.field}`)}</span>
                  <span className="text-base-content/80 break-all">
                    {shown(c.current)} → {shown(c.incoming)}
                  </span>
                  {!c.selectable && (
                    <span className="border border-warning px-2">{t('crmFieldBlocked')}</span>
                  )}
                </label>
              ))}
            </fieldset>
          )}
        </div>
      )}
    </li>
  );
}
